<template>
  <bm-text
    :value="internalValueString"
    v-bind="$attrs"
    :prepend-icon="bmIconName('AccessTime')"
    :error-messages="errors"
    :clearable="clearable"
    @blur="blurEvent"
    @update:value="onInput"
  />
</template>

<script setup lang="ts">
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import type { LocalTime } from "@js-joda/core";
import { ChronoUnit } from "@js-joda/core";
import { computed, ref, watch } from "vue";
import { useI18n } from "@/plugins/i18n";
import { makeRef } from "@/components/composables/reactivityTypes";
import { bmIconName } from "@/components/controls/bmIconMap";

interface BmTimeProps {
  value?: LocalTime | null;
  clearable?: boolean;
  errorMessages?: string[];
}

interface BmTimeEvents {
  (event: "update:value", value: LocalTime | null): void;
}

const props = withDefaults(defineProps<BmTimeProps>(), {
  errorMessages: () => [],
});

const emit = defineEmits<BmTimeEvents>();

const internalValueString = ref("");
const { $m } = useI18n();
const hasTextChanged = makeRef(false);

watch(
  () => props.value,
  (newValue) => {
    internalValueString.value = newValue
      ? newValue.truncatedTo(ChronoUnit.MINUTES).toString()
      : "";
  },
  { immediate: true }
);

const blurEvent = () => {
  const parsedTime = tenantService.parseFormattedTime(
    internalValueString.value
  );

  // Emit if valid time entered or time has been cleared
  if ((props.clearable && !internalValueString.value) || parsedTime) {
    emit("update:value", parsedTime);
  }
};

const onInput = (val: string) => {
  internalValueString.value = val;
  hasTextChanged.value = true;
};

const localErrors = computed(() => {
  const parsed = tenantService.parseFormattedTime(internalValueString.value);
  return !hasTextChanged.value ||
    (props.clearable && !internalValueString.value) ||
    parsed
    ? []
    : [$m.validation_invalid_time];
});

const errors = computed(() => [...localErrors.value, ...props.errorMessages]);
</script>
